import TravelRequirementsNotice from "../components/travel/TravelRequirementsNotice";
import React, {useCallback, useEffect, useState} from "react";
import {useFlightSearchStore} from "../store/useFlightSearchStore";
import {FlightSearchBar} from "../components/search/flightSearchBar";
import {SelectorType} from "../models/FlightSearch";
import {FlightSearchData} from "../models/flightSearchData";
import GroupTripButton from "../components/buttons/groupTripButton";
import HomeButton from "../components/buttons/homeButton";
import RoundTripList from "../components/list/RoundTripList";
import {FlightSearchService} from "../services/FlightSearchService";
import FlightSearchSelection from "../components/selector/FlightSearchSelection";
import RoundTripCardList from "../components/list/roundTripCardList";
import {RoundTripFlightMatch} from "../models/RoundTripFlightMatch";
import {useNavigate} from "react-router-dom";
import {TravelDurationMap} from "../models/TravelDuration";
import {ConnectionMode, TripType} from "../models/flightSearchData";
import {useTranslation} from "../i18n/LanguageProvider";
import {SearchRequest} from "../models/SearchRequest";
import OneWayFlightCalendarView from "../components/calendar/OneWayFlightCalendarView";
import { FiCalendar, FiList } from "react-icons/fi";

import MobileResults from "../mobile/MobileResults";
import useIsMobile from "../hooks/useIsMobile";

type ResultsView = "list" | "calendar";
const ListIcon = FiList as unknown as React.FC;
const CalendarIcon = FiCalendar as unknown as React.FC;


const SearchPage: React.FC = () => {
    const isMobile = useIsMobile();
    const searchData = useFlightSearchStore((state) => state.data);
    const [activeSelector, setActiveSelector] = useState<SelectorType | null>(null);
    const [flightSearchData, setFlightSearchData] = useState<FlightSearchData>(searchData);
    const [flightDeals, setFlightDeals] = useState<RoundTripFlightMatch[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [resultsView, setResultsView] = useState<ResultsView>("list");
    const navigate = useNavigate();
    const {t} = useTranslation();
    const isOneWay = flightSearchData.tripType === TripType.OneWay;


    const handleSearch = useCallback(async () => {
        try {
            if (!flightSearchData.dateFrom || !flightSearchData.dateTo || flightSearchData.airports.length === 0) {
                console.warn(t("warning.missingSearchData"));
                return;
            }

            setIsLoading(true);
            const durationMeta = TravelDurationMap[flightSearchData.travelDuration];
            const durationFrom =
                flightSearchData.travelDuration === "custom"
                    ? flightSearchData.minDurationDays ?? durationMeta.minDays
                    : durationMeta.minDays;
            const durationTo =
                flightSearchData.travelDuration === "custom"
                    ? flightSearchData.maxDurationDays ?? undefined
                    : durationMeta.maxDays ?? undefined;
            const payload: SearchRequest = {
                startAirport: flightSearchData.airports[0].code,
                startAirports: flightSearchData.airports.map((airport) => airport.code),
                dateFrom: flightSearchData.dateFrom.toISOString().split("T")[0],
                dateTo: flightSearchData.dateTo.toISOString().split("T")[0],
                tripType: flightSearchData.tripType,
                maxStops: flightSearchData.connectionMode === ConnectionMode.DirectAndOneStop ? 1 : 0,
                categories: flightSearchData.categories,
                durationFrom: isOneWay ? undefined : durationFrom,
                durationTo: isOneWay ? undefined : durationTo,
                destinationAirports: flightSearchData.destinationAirports.map((airport) => airport.code),
                destinationCountries: flightSearchData.destinationCountries.map((country) => country.code),
                travelProfile: flightSearchData.travelProfile,
            };

            console.log(payload);

            const results = await FlightSearchService.searchFlights(payload);
            setFlightDeals(results);
        } catch (error) {
            console.error("Search failed", error);
        } finally {
            setIsLoading(false);
        }
    }, [flightSearchData, isOneWay, t]);

    useEffect(() => {
        handleSearch();
        // Run only once on page entry; later searches are triggered by the search button.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isOneWay && resultsView === "calendar") {
            setResultsView("list");
        }
    }, [isOneWay, resultsView]);


    if (isMobile) {
    return <MobileResults flights={flightDeals} isLoading={isLoading} searchData={flightSearchData}
      setSearchData={setFlightSearchData} onSearch={handleSearch} />;
  }

  return (
        <div className="flex flex-col items-center justify-center gap-8 px-4 sm:pl-6 md:px-4 lg:pl-10 lg:pr-0 xl:px-28 py-5 sm:py-8 lg:py-10 transition-smooth pb-28 lg:pb-28">
            {/* Top Section */}
            <div className="flex flex-col md:flex-row lg:items-start xl:justify-between w-full gap-6 lg:gap-20">
                <div className="flex shrink-0 justify-start mt-1">
                    <HomeButton/>
                </div>

                <div className="w-full min-w-0 max-w-4xl flex flex-col gap-4">
                    <FlightSearchBar
                        activeSelector={activeSelector}
                        setActiveSelector={setActiveSelector}
                        searchData={flightSearchData}
                        setSearchData={setFlightSearchData}
                        onSearch={() => handleSearch()}
                    />

                    {activeSelector && (
                        <FlightSearchSelection
                            activeSelector={activeSelector}
                            setActiveSelector={setActiveSelector}
                            flightSearchData={flightSearchData}
                            setFlightSearchData={setFlightSearchData}
                        />
                    )}
                </div>

                <div className="hidden xl:block justify-end mt-1.5">
                    <GroupTripButton/>
                </div>
            </div>

      <TravelRequirementsNotice active={!!flightSearchData.travelProfile} empty={!isLoading && flightDeals.length === 0} />
            {/* Title */}
            <div className="w-full text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#2D2D2D] transition-all duration-300">
                    {t("page.searchTitle")}
                </h2>
            </div>

            {/* Results Section */}
            <div
                className="flex flex-col gap-10 justify-start w-full px-0 sm:px-4 md:pl-10 lg:pl-10 md:pr-10 max-w-[1168px] transition-all duration-300">
                {isLoading ? (
                    <div className="flex justify-center items-center h-40">
                        <div
                            className="animate-spin rounded-full h-12 w-12 border-4 border-[#712FFF] border-t-transparent"></div>
                    </div>
                ) : (
                    <>
                        {isOneWay && (
                            <div className="flex w-full justify-end">
                                <div className="flex items-center gap-1 rounded-2xl bg-white p-1.5 shadow-card ring-1 ring-[#F2F0FA]">
                                    <button
                                        onClick={() => setResultsView("list")}
                                        className={`flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-extrabold transition-smooth ${
                                            resultsView === "list"
                                                ? "bg-[#712FFF] text-white shadow-sm"
                                                : "text-[#8C82A8] hover:bg-[#F4F2FF]"
                                        }`}
                                    >
                                        <ListIcon/>
                                        {t("resultsView.list")}
                                    </button>
                                    <button
                                        onClick={() => setResultsView("calendar")}
                                        className={`flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-extrabold transition-smooth ${
                                            resultsView === "calendar"
                                                ? "bg-[#712FFF] text-white shadow-sm"
                                                : "text-[#8C82A8] hover:bg-[#F4F2FF]"
                                        }`}
                                    >
                                        <CalendarIcon/>
                                        {t("resultsView.calendar")}
                                    </button>
                                </div>
                            </div>
                        )}

                        {resultsView === "list" && (
                        <div className="hidden lg:flex flex-col items-start justify-start w-full gap-4">
                            <h2 className="text-[#D8D6E6] font-bold text-base">{t("page.hotPicks")}</h2>
                            <RoundTripCardList flightList={flightDeals}/>
                        </div>
                        )}

                        <div className="flex flex-col gap-3 w-full justify-start">
                            <h2 className="text-[#D8D6E6] font-bold text-base">
                                {t("page.resultsTitle")}
                            </h2>
                            {isOneWay && resultsView === "calendar" ? (
                                <OneWayFlightCalendarView
                                    flightList={flightDeals}
                                    dateFrom={flightSearchData.dateFrom}
                                    dateTo={flightSearchData.dateTo}
                                />
                            ) : (
                                <RoundTripList
                                    flightList={flightDeals}
                                    groupByDestination={isOneWay}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Bottom Button */}
            <div className="compare-action fixed inset-x-0 bottom-0 z-20 px-4 pt-3 w-full flex justify-center bg-white/95 sm:bg-transparent sm:bottom-7 sm:pt-0">
                <button
                    onClick={() => navigate("/compare")}
                    className="min-h-[44px] w-full sm:w-auto bg-accent px-6 sm:px-7 py-2.5 sm:py-3 text-white font-bold text-sm sm:text-base rounded-xl hover:bg-[#5b23cc] transition"
                >
                    {t("searchBar.compare")}
                </button>
            </div>
        </div>

    );
};

export default SearchPage;
